import React, { useMemo, useState } from 'react';
import { Table, Progress, ConfigProvider, theme, message } from 'antd';
import { Plus, List, Trello, Search, Settings, Tag, Download, MessageSquare, Paperclip, Clock, Moon, Sun } from 'lucide-react';
import Layout from '../../Layout';
import { useNavigate, Link } from "react-router-dom";
import useProjectStore from './store';
import useWorkStore from '../WorkList/store';
import { ME_ID, projectOf } from '../WorkList/data';
import { Avatar, fmtDate } from '../WorkList/ui';
import { PROJECT_ACTION_LABEL, participantsOf, progressOf, projectActions, projectTasks, type ProjectAction, type ProjectStatus } from './data';


// Nhãn trạng thái của màn danh sách ↔ trạng thái dự án
const STATUS_LABEL: Record<ProjectStatus, string> = { waiting: 'Đang chờ', doing: 'Đang thực hiện', paused: 'Tạm dừng', done: 'Hoàn thành', cancelled: 'Hủy' };

// Kéo thẻ sang cột = thao tác trạng thái tương ứng
const dropAction = (from: ProjectStatus, to: string): ProjectAction | null => {
    if (to === 'Đang thực hiện') return from === 'waiting' ? 'start' : from === 'paused' ? 'resume' : from === 'done' || from === 'cancelled' ? 'reopen' : null;
    if (to === 'Tạm dừng') return 'pause';
    if (to === 'Hoàn thành') return 'finish';
    if (to === 'Hủy') return 'cancel';
    return null;
};

export default function ProjectList() {
    const navigate = useNavigate();
    const [viewMode, setViewMode] = useState<'list' | 'kanban'>('list');
    const [activeTab, setActiveTab] = useState('Đang thực hiện');
    const [q, setQ] = useState('');
    const infos = useProjectStore(s => s.projects);
    const act = useProjectStore(s => s.act);
    const allTasks = useWorkStore(s => s.tasks);
    const isDarkMode = document.documentElement.classList.contains('dark');

    const projects = useMemo(() => infos.map(p => ({
        id: p.id,
        code: p.code,
        name: projectOf(p.id)?.name ?? p.id,
        status: STATUS_LABEL[p.status],
        progress: progressOf(p, allTasks),
        startDate: fmtDate(p.startAt),
        endDate: fmtDate(p.endAt),
        managers: p.ownerIds,
        participants: participantsOf(p, allTasks).filter(id => !p.ownerIds.includes(id)),
        tasks: projectTasks(p.id, allTasks).length,
        comments: p.activity.filter(x => x.type === 'comment').length,
        attachments: p.links.length,
        image: null as string | null,
    })), [infos, allTasks]);

    const filteredProjects = projects.filter(p => !q || `${p.code} ${p.name}`.toLowerCase().includes(q.toLowerCase())).filter(p => {
        if (activeTab === 'Tất cả') return true;
        if (activeTab === 'Đang thực hiện') return p.status === 'Đang thực hiện';
        if (activeTab === 'Hoàn thành') return p.status === 'Hoàn thành';
        if (activeTab === 'Tạm dừng') return p.status === 'Tạm dừng';
        if (activeTab === 'Hủy') return p.status === 'Hủy';
        if (activeTab === 'Đang chờ') return p.status === 'Đang chờ';
        return true;
    });

    const columns = [
        {
            title: 'NGƯỜI QUẢN TRỊ',
            key: 'managers',
            width: 140,
            render: (_: any, record: any) => (
                <div className="flex items-center -space-x-1.5 overflow-hidden">
                    {record.managers.map((id: string) => <span key={id} className="rounded-full ring-2 ring-white dark:ring-gray-800"><Avatar id={id} size={24} /></span>)}
                </div>
            )
        },
        {
            title: 'MÃ DỰ ÁN',
            dataIndex: 'code',
            key: 'code',
            width: 180,
            render: (text: string) => <span className="text-[#3e91ab] dark:text-[#5bc0de] font-medium text-xs">{text}</span>
        },
        {
            title: 'TÊN DỰ ÁN',
            dataIndex: 'name',
            key: 'name',
            width: 240,
            render: (text: string) => <span className="text-gray-800 dark:text-gray-200 font-medium text-xs hover:text-[#3e91ab] dark:hover:text-[#5bc0de] cursor-pointer">{text}</span>
        },
        {
            title: 'NGƯỜI THAM GIA',
            key: 'participants',
            width: 160,
            render: (_: any, record: any) => (
                <div className="flex items-center -space-x-1.5 overflow-hidden">
                    {record.participants.slice(0, 6).map((id: string) => <span key={id} className="rounded-full ring-2 ring-white dark:ring-gray-800"><Avatar id={id} size={24} /></span>)}
                    {record.participants.length > 6 && <span className="pl-2.5 text-[11px] text-gray-500">+{record.participants.length - 6}</span>}
                    {(!record.participants || record.participants.length === 0) && <span className="text-gray-400 dark:text-gray-500 text-xs">--</span>}
                </div>
            )
        },
        {
            title: 'TIẾN ĐỘ',
            dataIndex: 'progress',
            key: 'progress',
            width: 150,
            render: (val: number) => (
                <div className="flex items-center space-x-2">
                    <span className="text-[11px] font-semibold text-gray-600 dark:text-gray-300 w-8">{val}%</span>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 h-1.5 rounded-full overflow-hidden">
                        <div className="bg-[#3e91ab] h-full" style={{ width: `${val}%` }}></div>
                    </div>
                </div>
            )
        },
        {
            title: 'CÔNG VIỆC',
            dataIndex: 'tasks',
            key: 'tasks',
            width: 90,
            render: (val: number) => <span className="text-xs text-gray-600 dark:text-gray-300">{val}</span>
        },
        {
            title: 'BẮT ĐẦU',
            dataIndex: 'startDate',
            key: 'startDate',
            width: 110,
            render: (text: string) => <span className="text-xs text-gray-600 dark:text-gray-300">{text}</span>
        },
        {
            title: 'KẾT THÚC',
            dataIndex: 'endDate',
            key: 'endDate',
            width: 110,
            render: (text: string) => <span className="text-xs text-gray-600 dark:text-gray-300">{text}</span>
        },
    ];

    const handleDragStart = (e: React.DragEvent, id: string) => e.dataTransfer.setData('projectId', id);
    const handleDrop = (e: React.DragEvent, status: string) => {
        const id = e.dataTransfer.getData('projectId');
        const p = infos.find(x => x.id === id);
        if (!p || STATUS_LABEL[p.status] === status) return;
        const a = dropAction(p.status, status);
        if (!a || !projectActions(p, ME_ID).includes(a)) { message.warning('Không chuyển dự án sang trạng thái này được'); return; }
        if (a === 'pause' || a === 'cancel') { message.info(`${PROJECT_ACTION_LABEL[a]} cần ghi lý do — thực hiện trong chi tiết dự án`); navigate(`/projects/detail?id=${id}`); return; }
        const err = act(id, a, ME_ID);
        if (err) message.warning(err); else message.success(PROJECT_ACTION_LABEL[a]);
    };

    const kanbanColumns = [
        { name: 'Đang thực hiện', color: 'bg-red-500' },
        { name: 'Hoàn thành', color: 'bg-pink-600' },
        { name: 'Tạm dừng', color: 'bg-purple-600' },
        { name: 'Hủy', color: 'bg-indigo-700' },
        { name: 'Đang chờ', color: 'bg-blue-700' },
    ];

    const tabsList = [
        { name: 'Đang thực hiện', count: projects.filter(p => p.status === 'Đang thực hiện').length },
        { name: 'Hoàn thành', count: projects.filter(p => p.status === 'Hoàn thành').length },
        { name: 'Tạm dừng', count: projects.filter(p => p.status === 'Tạm dừng').length },
        { name: 'Hủy', count: projects.filter(p => p.status === 'Hủy').length },
        { name: 'Đang chờ', count: projects.filter(p => p.status === 'Đang chờ').length },
        { name: 'Tất cả', count: projects.length },
    ];

    return (
        <Layout>
            <ConfigProvider
                theme={{
                    algorithm: isDarkMode ? theme.darkAlgorithm : theme.defaultAlgorithm,
                }}
            >
                <div className="bg-white dark:bg-gray-900 flex flex-col text-xs transition-colors duration-200">
                    
                    {/* 1. Thanh tìm kiếm và chuyển đổi chế độ */}
                    <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 px-4 py-3 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
                        <div className="flex items-center space-x-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-3 py-1.5 rounded-md w-full sm:w-72">
                            <Search size={14} className="text-gray-400 dark:text-gray-500 shrink-0" />
                            <input 
                                type="text" 
                                placeholder="Tìm mã, tên dự án"
                                value={q}
                                onChange={e => setQ(e.target.value)} 
                                className="bg-transparent border-none outline-none text-xs w-full text-gray-700 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500" 
                            />
                        </div>

                        <div className="flex items-center justify-between sm:justify-end space-x-2 sm:space-x-3">

                            <div className="flex items-center bg-gray-100 dark:bg-gray-800 p-0.5 rounded-md border border-gray-200 dark:border-gray-700">
                                <button 
                                    onClick={() => setViewMode('list')}
                                    className={`flex items-center px-3 py-1.5 rounded font-medium transition-all ${
                                        viewMode === 'list' 
                                            ? 'bg-white dark:bg-gray-700 text-gray-800 dark:text-white shadow-sm' 
                                            : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-white'
                                    }`}
                                >
                                    <List size={13} className="mr-1.5" /> Danh sách
                                </button>
                                <button 
                                    onClick={() => setViewMode('kanban')}
                                    className={`flex items-center px-3 py-1.5 rounded font-medium transition-all ${
                                        viewMode === 'kanban' 
                                            ? 'bg-white dark:bg-gray-700 text-gray-800 dark:text-white shadow-sm' 
                                            : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-white'
                                    }`}
                                >
                                    <Trello size={13} className="mr-1.5" /> Kanban
                                </button>
                            </div>

                            <Link to="/projects/add" className="flex items-center justify-center px-3.5 py-1.5 bg-[#3e91ab] text-white font-medium rounded shadow-sm transition whitespace-nowrap">
                                <Plus size={14} className="mr-1.5" /> Thêm mới
                            </Link>
                        </div>
                    </div>

                    {/* 2. Thanh Tabs trạng thái (Chỉ hiện ở chế độ Danh sách) */}
                    {viewMode === 'list' && (
                        <div className="flex items-center px-4 border-b border-gray-200 dark:border-gray-800 overflow-x-auto space-x-6 bg-white dark:bg-gray-900 scrollbar-none">
                            {tabsList.map(tab => (
                                <button
                                    key={tab.name}
                                    onClick={() => setActiveTab(tab.name)}
                                    className={`py-2.5 font-medium whitespace-nowrap border-b-2 transition-colors shrink-0 ${
                                        activeTab === tab.name 
                                            ? 'border-[#3e91ab] text-[#3e91ab] dark:text-orange-400' 
                                            : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                                    }`}
                                >
                                    {tab.name} <span className="text-gray-400 dark:text-gray-500 font-normal">({tab.count})</span>
                                </button>
                            ))}
                        </div>
                    )}

                    {/* 3. Thanh công cụ phụ (Chỉ hiển thị khi ở chế độ Danh sách) */}
                    {viewMode === 'list' && (
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center px-4 py-2 gap-2 bg-gray-50/50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-800 text-gray-500 dark:text-gray-400">
                            <span className="text-gray-600 dark:text-gray-400">Hiển thị 1 - {filteredProjects.length} / {filteredProjects.length} bản ghi</span>
                            <div className="flex items-center space-x-4 overflow-x-auto w-full sm:w-auto pb-1 sm:pb-0">
                                <button className="flex items-center hover:text-[#3e91ab] dark:hover:text-[#5bc0de] shrink-0"><Tag size={13} className="mr-1" /> Nhãn</button>
                                <button className="flex items-center hover:text-[#3e91ab] dark:hover:text-[#5bc0de] shrink-0"><Download size={13} className="mr-1" /> Import</button>
                                <button className="flex items-center hover:text-[#3e91ab] dark:hover:text-[#5bc0de] shrink-0"><Download size={13} className="mr-1" /> Export</button>
                                <button onClick={() => navigate(`/projects/setup`)} className="flex items-center hover:text-[#3e91ab] dark:hover:text-[#5bc0de] shrink-0"><Settings size={13} className="mr-1" /> Cài đặt</button>
                            </div>
                        </div>
                    )}

                    {/* 4. Nội dung chính */}
                    <div className="p-3 sm:p-4 bg-gray-50/30 dark:bg-gray-950 flex-1">
                        {viewMode === 'list' ? (
                            <div className="w-full">
                                <Table 
                                    columns={columns} 
                                    dataSource={filteredProjects} 
                                    rowKey="id"
                                    pagination={false}
                                    rowSelection={{ type: 'checkbox' }}
                                    scroll={{ x: 'max-content' }}
                                    className="border border-gray-200 dark:border-gray-800 rounded-md"
                                    onRow={(record) => ({
                                        onClick: () => {
                                            // Chuyển hướng sang trang chi tiết kèm theo ID của dự án (hoặc tùy chỉnh đường dẫn theo ý bạn)
                                            navigate(`/projects/detail?id=${record.id}`);
                                        },
                                        className: "cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                                    })}
                                />
                            </div>
                        ) : (
                            <div className="flex gap-4 h-full overflow-x-auto pb-4 items-start">
                                {kanbanColumns.map(col => {
                                    const colProjects = projects.filter(p => p.status === col.name);
                                    return (
                                        <div 
                                            key={col.name} 
                                            className="w-72 flex-shrink-0 bg-gray-100 dark:bg-gray-900 rounded-lg flex flex-col border border-gray-200 dark:border-gray-800 max-h-[calc(100vh-180px)]"
                                            onDragOver={(e) => e.preventDefault()}
                                            onDrop={(e) => handleDrop(e, col.name)}
                                        >
                                            <div className={`p-2.5 ${col.color} text-white font-medium rounded-t-lg mb-2 flex justify-between items-center shadow-sm shrink-0`}>
                                                <span>{col.name} ({colProjects.length})</span>
                                                <span className="text-white/80 text-[11px] cursor-pointer">...</span>
                                            </div>

                                            <div className="flex-1 p-2 space-y-2.5 overflow-y-auto">
                                                {colProjects.map(p => (
                                                    <div 
                                                        key={p.id} 
                                                        draggable 
                                                        onDragStart={(e) => handleDragStart(e, p.id)}
                                                        className="bg-white dark:bg-gray-800 p-3 rounded-md shadow-sm cursor-grab active:cursor-grabbing border border-gray-200 dark:border-gray-700 hover:shadow-md transition-all"
                                                    >
                                                        {p.image && (
                                                            <div className="mb-2.5 rounded overflow-hidden h-28 border border-gray-100 dark:border-gray-700">
                                                                <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                                                            </div>
                                                        )}

                                                        <div className="font-semibold text-gray-800 dark:text-gray-100 mb-2 leading-snug">{p.code} - {p.name}</div>
                                                        
                                                        <div className="my-2">
                                                            <div className="flex justify-between text-[10px] text-gray-500 dark:text-gray-400 mb-1">
                                                                <span>Tiến độ</span>
                                                                <span>{p.progress}%</span>
                                                            </div>
                                                            <Progress percent={p.progress} size="small" showInfo={false} strokeColor="#3e91ab" />
                                                        </div>

                                                        <div className="flex justify-between items-center mt-3 pt-2 border-t border-gray-100 dark:border-gray-700 text-[10px] text-gray-500 dark:text-gray-400">
                                                            <div className="flex items-center space-x-2.5">
                                                                <span className="flex items-center"><MessageSquare size={11} className="mr-0.5 text-gray-400 dark:text-gray-500" /> {p.comments}</span>
                                                                <span className="flex items-center"><Paperclip size={11} className="mr-0.5 text-gray-400 dark:text-gray-500" /> {p.attachments}</span>
                                                            </div>
                                                            <div className="flex items-center space-x-1.5">
                                                                <span className="flex items-center"><Clock size={11} className="mr-1 text-gray-400 dark:text-gray-500" /> {p.startDate}</span>
                                                                <div className="flex items-center -space-x-1.5 overflow-hidden">
                                                                    {p.managers.map(id => <span key={id} className="rounded-full ring-1 ring-white dark:ring-gray-800"><Avatar id={id} size={20} /></span>)}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                                {colProjects.length === 0 && (
                                                    <div className="text-center py-6 text-gray-400 dark:text-gray-600 italic text-xs">Không có dự án</div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                </div>
            </ConfigProvider>
        </Layout>
    );
}